import { useEffect, useRef, useState } from 'react';

import { ListingType } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerPropertyAddressDetailsWebApiHandler_details,
  useRentalManagerPropertyAddressVerificationWebApiHandler_verification,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { isUnitNumberOverLimit, resolveUnitAlert, validateNumUnits } from '../utils';

import { DEBOUNCE_TIME_500_MS, PROPERTY_CODES_BY_TYPE, PROPERTY_TYPES, PROPERTY_TYPES_BY_ENUM } from '../constants';

import { useAddressAutocomplete } from './useAddressAutocomplete';
import { usePropertyLocationCheck } from './usePropertyLocationCheck';

import type { ReactNode } from 'react';
import type { ChangeEvent } from 'react';
import type { ComboboxOptionType } from '@zillow/constellation-10';
import type {
  RentalManagerAuthCheckUserInfoWebApiDto,
  RentalManagerPropertyAddressDetailsDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { UnitAlertValue } from '../constants';
import type { AddressComponents } from '../types';
import type { ImageValue } from '../types';

const { COMMUNITY, CONDO, MULTIPLEX } = PROPERTY_TYPES;

const restrictionKey = (
  currentAddress: string,
  currentUnit: string,
  currentPropertyType: string,
  currentListingType: ListingType,
) => `${currentAddress}|${currentUnit}|${currentPropertyType}|${currentListingType}`;

const listingTypeFor = (roomForRent: boolean) => (roomForRent ? ListingType.ROOM : ListingType.RENTAL);

const isRoomForRentEligible = (currentPropertyType: string) =>
  currentPropertyType !== MULTIPLEX && currentPropertyType !== COMMUNITY;

const isAddableStreetEasy = (result?: { restrictedStreetEasy?: boolean; restrictedOutEast?: boolean } | null) =>
  Boolean(result?.restrictedStreetEasy) && !result?.restrictedOutEast;

export type AddPropertyDetailsState = {
  address: AddressComponents;
  isRoomForRent: boolean;
  propertyType: string;
  numUnits: string;
  image: File | null;
  imagePreviewUrl: string | null;
  isImpreciseLocation: boolean;
  listingAlias: string;
  isStreetEasyRestricted: boolean;
};

type Params = {
  onContinue: (state: AddPropertyDetailsState) => void;
  user: RentalManagerAuthCheckUserInfoWebApiDto | null;
};

export const useAddPropertyDetailsForm = ({ onContinue, user }: Params) => {
  const [address, setAddress] = useState('');
  const [selectedAddress, setSelectedAddress] = useState('');
  const [isRoomForRent, setIsRoomForRent] = useState(false);
  const [propertyType, setPropertyType] = useState('');
  const [unit, setUnit] = useState('');
  const [numUnits, setNumUnits] = useState('');
  const [imageValue, setImageValue] = useState<ImageValue>({ image: null, imagePreviewUrl: null });
  const [addressError, setAddressError] = useState('');
  const [propertyTypeError, setPropertyTypeError] = useState('');
  const [numUnitsError, setNumUnitsError] = useState('');
  const [propertyAlreadyPostedWarningMessage, setPropertyAlreadyPostedWarningMessage] = useState<
    string | Array<ReactNode>
  >('');
  const [unitAlert, setUnitAlert] = useState<UnitAlertValue | null>(null);
  const [isStreetEasyRestricted, setIsStreetEasyRestricted] = useState(false);

  const addressInputRef = useRef<HTMLInputElement>(null);
  const prevVerifiedRef = useRef({ address: '', unit: '' });
  const lastVerifiedDataRef = useRef<RentalManagerPropertyAddressDetailsDto | null>(null);
  const lastRestrictionKeyRef = useRef<string | undefined>(undefined);

  const [setPartialAddress, queryData] = useAddressAutocomplete({
    debounceMs: DEBOUNCE_TIME_500_MS,
  });

  const { data: addressDetailsData, isFetching: isLoadingAddressDetails } =
    useRentalManagerPropertyAddressDetailsWebApiHandler_details(
      { fullAddress: selectedAddress, unit },
      {
        enabled: !!selectedAddress && selectedAddress === address,
        meta: {
          tags: ['address-details'],
          context: {
            api: 'PropertyAddressDetailsWebApiHandler',
            details: 'Auto-populate property type, beds, and baths from address',
          },
        },
      },
    );

  useEffect(() => {
    if (addressDetailsData?.propertyType && !propertyType) {
      const detectedType = PROPERTY_TYPES_BY_ENUM[addressDetailsData.propertyType];
      if (detectedType) setPropertyType(detectedType);
    }
  }, [addressDetailsData, propertyType]);

  const { isCheckingRestrictions, checkLocationRestriction: checkRestrictions } = usePropertyLocationCheck({
    user: user ?? undefined,
    setPropertyAlreadyPostedWarningMessage,
  });

  const inFlightRestrictionRef = useRef<
    | {
        key: string;
        skipMessage: boolean;
        promise: ReturnType<typeof checkRestrictions>;
      }
    | undefined
  >(undefined);

  // Shared only when the in-flight request produces at least as much as the caller needs.
  const runRestrictionCheck = (
    currentAddress: string,
    currentUnit: string,
    currentPropertyType: string,
    skipMessage: boolean,
    currentListingType: ListingType,
  ) => {
    const key = restrictionKey(currentAddress, currentUnit, currentPropertyType, currentListingType);
    const inFlight = inFlightRestrictionRef.current;
    if (inFlight?.key === key && (skipMessage || !inFlight.skipMessage)) {
      return inFlight.promise;
    }

    const promise = checkRestrictions({
      address: currentAddress,
      unit: currentUnit,
      propertyType: currentPropertyType,
      listingType: currentListingType,
      skipMessage,
    }).finally(() => {
      const current = inFlightRestrictionRef.current;
      if (current?.key === key && current.skipMessage === skipMessage) {
        inFlightRestrictionRef.current = undefined;
      }
    });
    inFlightRestrictionRef.current = { key, skipMessage, promise };

    return promise;
  };

  const checkLocationRestriction = async (
    currentAddress: string,
    currentPropertyType: string,
    currentIsRoomForRent = isRoomForRent,
  ) => {
    const currentListingType = listingTypeFor(currentIsRoomForRent && isRoomForRentEligible(currentPropertyType));
    const result = await runRestrictionCheck(currentAddress, unit, currentPropertyType, false, currentListingType);
    lastRestrictionKeyRef.current = restrictionKey(currentAddress, unit, currentPropertyType, currentListingType);
    const isAddableStreetEasyLocation = isAddableStreetEasy(result);
    setIsStreetEasyRestricted(isAddableStreetEasyLocation);

    if (result?.errorMessage && !isAddableStreetEasyLocation) {
      setPropertyAlreadyPostedWarningMessage(result.errorMessage);
    }
    if (result?.paidMultifamilyLocation && !currentPropertyType) {
      setPropertyType(unit ? CONDO : COMMUNITY);
    }
  };

  const toggleRoomForRent = () => {
    const nextIsRoomForRent = !isRoomForRent;
    setIsRoomForRent(nextIsRoomForRent);
    if (address) void checkLocationRestriction(address, propertyType, nextIsRoomForRent);
  };

  const propertyTypeCode = (PROPERTY_CODES_BY_TYPE as Record<string, number>)[propertyType];

  const { refetch: verifyAddress, isFetching: isVerifyingAddress } =
    useRentalManagerPropertyAddressVerificationWebApiHandler_verification(
      { fullAddress: address, unit, propertyTypeCode },
      {
        enabled: false,
        meta: {
          tags: ['address-verification'],
          context: { api: 'PropertyAddressVerificationWebApiHandler', details: 'Verify address on continue' },
        },
      },
    );

  const handleAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAddress(e.target.value);
    setAddressError('');
    setPropertyAlreadyPostedWarningMessage('');
    setIsStreetEasyRestricted(false);
    setPartialAddress(e.target.value);
  };

  const handleSelectAddress = (option: ComboboxOptionType) => {
    const value = typeof option === 'string' ? option : option.value;
    setAddress(value);
    setSelectedAddress(value);
    setAddressError('');
    void checkLocationRestriction(value, propertyType);
  };

  const handlePropertyTypeChange = (value: string) => {
    setPropertyType(value);
    setPropertyTypeError('');
    setUnit('');
    setNumUnits('');
    setNumUnitsError('');
    setUnitAlert(null);
    void checkLocationRestriction(address, value);
  };

  const handleUnitChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUnit(e.target.value);
  };

  const handleNumUnitsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNumUnits(value);
    if (value) {
      const error = validateNumUnits(value, propertyType);
      setNumUnitsError(error ?? '');
    } else {
      setNumUnitsError('');
    }
  };

  const advanceToReview = (data: RentalManagerPropertyAddressDetailsDto, streetEasyRestricted?: boolean) => {
    onContinue({
      address: {
        street: data.street,
        city: data.city,
        state: data.state,
        zip: data.zip,
        unit: data.unitWithoutLabel || unit,
      },
      isRoomForRent: isRoomForRent && isRoomForRentEligible(propertyType),
      propertyType,
      numUnits,
      image: imageValue.image,
      imagePreviewUrl: imageValue.imagePreviewUrl,
      isImpreciseLocation: data.isInaccurateGeoPrecision ?? false,
      listingAlias: data.propertyId != null ? String(data.propertyId) : '',
      isStreetEasyRestricted: streetEasyRestricted ?? isStreetEasyRestricted,
    });
  };

  const handleContinue = async () => {
    let hasError = false;
    if (!address.trim()) {
      setAddressError('Street address is required');
      hasError = true;
    }
    if (!propertyType) {
      setPropertyTypeError('Property type is required');
      hasError = true;
    }
    const numUnitsValidationError = validateNumUnits(numUnits, propertyType);
    if (numUnitsValidationError) {
      setNumUnitsError(numUnitsValidationError);
      hasError = true;
    }
    if (isUnitNumberOverLimit(unit)) {
      hasError = true;
    }
    if (hasError) return;

    // User already saw a unit alert for this same address+unit — acknowledge and proceed
    if (
      unitAlert !== null &&
      prevVerifiedRef.current.address === address &&
      prevVerifiedRef.current.unit === unit &&
      lastVerifiedDataRef.current
    ) {
      setUnitAlert(null);
      advanceToReview(lastVerifiedDataRef.current);
      return;
    }

    // A typed address reaches here unchecked, since the check is bound to select/type-change.
    const listingType = listingTypeFor(isRoomForRent && isRoomForRentEligible(propertyType));
    const needsRestrictionRefresh =
      lastRestrictionKeyRef.current !== restrictionKey(address, unit, propertyType, listingType);
    const [{ data, error }, refreshedRestrictions] = await Promise.all([
      verifyAddress(),
      needsRestrictionRefresh
        ? runRestrictionCheck(address, unit, propertyType, true, listingType)
        : Promise.resolve(undefined),
    ]);
    if (needsRestrictionRefresh) {
      lastRestrictionKeyRef.current = restrictionKey(address, unit, propertyType, listingType);
      setIsStreetEasyRestricted(isAddableStreetEasy(refreshedRestrictions));
    }
    const streetEasyRestricted = needsRestrictionRefresh
      ? isAddableStreetEasy(refreshedRestrictions)
      : isStreetEasyRestricted;

    if (error || !data) {
      setAddressError('Address couldn’t be verified. Check the address and try again.');
      return;
    }

    prevVerifiedRef.current = { address, unit };
    lastVerifiedDataRef.current = data;

    if (data.unitWithoutLabel) {
      setUnit(data.unitWithoutLabel);
    }

    // Verification can echo back a normalized unit longer than the limit — stop here so the
    // field shows the error instead of the server rejecting it under Street address.
    if (isUnitNumberOverLimit(data.unitWithoutLabel || unit)) {
      return;
    }

    const isMultiUnitType = propertyType === COMMUNITY || propertyType === MULTIPLEX;
    const alert = isMultiUnitType ? null : resolveUnitAlert(data, unit);
    if (alert !== null) {
      setUnitAlert(alert);
      return;
    }

    setUnitAlert(null);
    advanceToReview(data, streetEasyRestricted);
  };

  return {
    address,
    isRoomForRent: isRoomForRent && isRoomForRentEligible(propertyType),
    isRoomForRentEligible: isRoomForRentEligible(propertyType),
    toggleRoomForRent,
    propertyType,
    unit,
    numUnits,
    imageValue,
    addressError,
    propertyTypeError,
    numUnitsError,
    propertyAlreadyPostedWarningMessage,
    isStreetEasyRestricted,
    unitAlert,
    isVerifyingAddress,
    isPropertyTypeDisabled: (isLoadingAddressDetails && !propertyType) || isCheckingRestrictions,
    addressInputRef,
    addressDetails: addressDetailsData,
    queryData,
    handleAddressChange,
    handleSelectAddress,
    handlePropertyTypeChange,
    handleUnitChange,
    handleNumUnitsChange,
    handleContinue,
    setImageValue,
  };
};
